'use client';

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, ChevronDown, Plus, Check, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { TagBadge, TagBadgeList } from '@/components/ui/tag-badge';
import { cn } from '@/lib/utils';
import { useTags, useTagsGrouped, useTagSearch } from '@/hooks/use-tags';
import {
  TAG_TYPES,
  TAG_TYPE_LABELS,
  TAGS_BY_TYPE,
  USER_TAG_TYPE_LABELS,
  USER_TAGS_BY_TYPE,
  getTagById,
} from '@/data/forum-tags';

/**
 * TagSelector - Component for selecting tags on discussions
 *
 * Features:
 * - Search/filter tags
 * - Grouped by type display
 * - Multi-select with max limit
 * - Selected tags preview
 * - Keyboard navigation
 */

const MAX_TAGS = 5;

export function TagSelector({
  value = [],
  onChange,
  maxTags = MAX_TAGS,
  placeholder = 'Add tags...',
  disabled = false,
  error,
  className,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState(null);
  const searchInputRef = useRef(null);

  // Fetch tags (with static fallback)
  const { data: tagsGrouped } = useTagsGrouped({ useStatic: true });
  const { data: searchResults } = useTagSearch(search, {
    enabled: search.length >= 2,
  });

  // Filter and organize tags (only user-selectable tags, excludes STATUS and SPECIAL)
  const displayTags = useMemo(() => {
    if (search && searchResults) {
      // Filter search results to only include user-selectable tags
      const filteredResults = searchResults.filter(
        (tag) => tag.type !== TAG_TYPES.STATUS && tag.type !== TAG_TYPES.SPECIAL
      );
      return { search: filteredResults };
    }

    if (activeType && USER_TAGS_BY_TYPE[activeType]) {
      return { [activeType]: USER_TAGS_BY_TYPE[activeType] };
    }

    // Use user-selectable tags only (excludes STATUS and SPECIAL)
    return USER_TAGS_BY_TYPE;
  }, [search, searchResults, activeType]);

  // Selected tag objects
  const selectedTags = useMemo(() => {
    return value.map((id) => getTagById(id)).filter(Boolean);
  }, [value]);

  // Check if a tag is selected
  const isSelected = useCallback((tagId) => value.includes(tagId), [value]);

  // Toggle tag selection
  const toggleTag = useCallback(
    (tag) => {
      const tagId = tag.id || tag.tagId;

      if (isSelected(tagId)) {
        // Remove tag
        onChange(value.filter((id) => id !== tagId));
      } else if (value.length < maxTags) {
        // Add tag
        onChange([...value, tagId]);
      }
    },
    [value, onChange, isSelected, maxTags]
  );

  // Remove a selected tag
  const removeTag = useCallback(
    (tag) => {
      const tagId = tag.id || tag.tagId;
      onChange(value.filter((id) => id !== tagId));
    },
    [value, onChange]
  );

  // Clear all tags
  const clearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  // Focus search on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset search and filter on close
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setActiveType(null);
    }
  }, [isOpen]);

  const canAddMore = value.length < maxTags;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Selected tags preview */}
      {selectedTags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <TagBadgeList
            tags={selectedTags}
            size="md"
            removable
            onRemove={removeTag}
          />
          {selectedTags.length > 1 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearAll}
              className="h-7 px-2 text-xs text-zinc-400 hover:text-zinc-300"
            >
              Clear all
            </Button>
          )}
        </div>
      )}

      {/* Tag selector popover */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            disabled={disabled || !canAddMore}
            className={cn(
              'justify-between min-w-[200px] bg-zinc-900/50 border-white/10',
              'hover:bg-zinc-900/80 hover:border-white/20',
              error && 'border-red-500/50',
              !canAddMore && 'opacity-50'
            )}
          >
            <span className="flex items-center gap-2 text-zinc-400">
              <Plus className="h-4 w-4" />
              {canAddMore
                ? `${placeholder} (${maxTags - value.length} left)`
                : `Max ${maxTags} tags`}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-[380px] p-0 bg-zinc-900 border-white/10"
          align="start"
        >
          <div className="p-3 border-b border-white/10">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                ref={searchInputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tags..."
                className="pl-9 bg-zinc-800/50 border-white/10 focus:border-violet-500/50"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="h-4 w-4 text-zinc-500 hover:text-zinc-300" />
                </button>
              )}
            </div>

            {/* Type filters (only user-selectable types) */}
            {!search && (
              <div className="flex gap-1 mt-2 flex-wrap">
                <Button
                  type="button"
                  variant={activeType === null ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveType(null)}
                  className="h-7 text-xs"
                >
                  All
                </Button>
                {Object.entries(USER_TAG_TYPE_LABELS).map(([type, label]) => (
                  <Button
                    key={type}
                    type="button"
                    variant={activeType === type ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveType(type)}
                    className="h-7 text-xs"
                  >
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Tags list */}
          <ScrollArea className="h-[300px]">
            <div className="p-3 space-y-4">
              {Object.entries(displayTags).map(([type, tags]) => {
                if (!tags || tags.length === 0) return null;

                const typeLabel =
                  USER_TAG_TYPE_LABELS[type] || (search ? 'Results' : type);

                return (
                  <div key={type}>
                    <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2">
                      {typeLabel}
                    </h4>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map((tag) => {
                        const tagId = tag.id || tag.tagId;
                        const selected = isSelected(tagId);
                        const canSelect = selected || canAddMore;

                        return (
                          <button
                            key={tagId}
                            type="button"
                            onClick={() => canSelect && toggleTag(tag)}
                            disabled={!canSelect}
                            className={cn(
                              'relative group',
                              !canSelect && 'opacity-40 cursor-not-allowed'
                            )}
                          >
                            <TagBadge
                              tag={tag}
                              size="md"
                              showIcon
                              className={cn(
                                'transition-all',
                                selected &&
                                  'ring-2 ring-violet-500 ring-offset-1 ring-offset-zinc-900',
                                canSelect && 'hover:scale-105'
                              )}
                            />
                            {selected && (
                              <motion.span
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute -top-1 -right-1 h-4 w-4 bg-violet-500 rounded-full flex items-center justify-center"
                              >
                                <Check className="h-2.5 w-2.5 text-white" />
                              </motion.span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Empty state */}
              {search && (!searchResults || searchResults.length === 0) && (
                <div className="text-center py-8 text-zinc-500">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No tags found for "{search}"</p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="p-3 border-t border-white/10 bg-zinc-800/50">
            <div className="flex items-center justify-between text-xs text-zinc-500">
              <span>
                {value.length} of {maxTags} tags selected
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-7"
              >
                Done
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Error message */}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-1.5 text-sm text-red-400"
          >
            <AlertCircle className="h-4 w-4" />
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * TagFilter - Simplified tag filter for listing pages
 */
export function TagFilter({
  value = [],
  onChange,
  showTypeFilter = false,
  multiple = true,
  className,
}) {
  const { data: tagsGrouped } = useTagsGrouped({ useStatic: true });
  const [activeType, setActiveType] = useState(null);

  const toggleTag = useCallback(
    (tagId) => {
      if (multiple) {
        if (value.includes(tagId)) {
          onChange(value.filter((id) => id !== tagId));
        } else {
          onChange([...value, tagId]);
        }
      } else {
        onChange(value.includes(tagId) ? [] : [tagId]);
      }
    },
    [value, onChange, multiple]
  );

  const displayTags = activeType
    ? tagsGrouped?.[activeType] || []
    : Object.values(tagsGrouped || {}).flat();

  return (
    <div className={cn('space-y-2', className)}>
      {showTypeFilter && (
        <div className="flex gap-1 flex-wrap">
          <Button
            type="button"
            variant={activeType === null ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setActiveType(null)}
          >
            All
          </Button>
          {Object.entries(TAG_TYPE_LABELS).map(([type, label]) => (
            <Button
              key={type}
              type="button"
              variant={activeType === type ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setActiveType(type)}
            >
              {label}
            </Button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5">
        {displayTags.slice(0, 20).map((tag) => {
          const tagId = tag.id || tag.tagId;
          const selected = value.includes(tagId);

          return (
            <button key={tagId} type="button" onClick={() => toggleTag(tagId)}>
              <TagBadge
                tag={tag}
                size="sm"
                className={cn(
                  selected &&
                    'ring-2 ring-violet-500 ring-offset-1 ring-offset-zinc-900'
                )}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default TagSelector;
